
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from '@/components/ui/tabs';
import { ArticleSettingsProps } from './types';
import MetadataTab from './MetadataTab';
import PublishingTab from './PublishingTab';
import SeoTab from './SeoTab';

const ArticleSettings: React.FC<ArticleSettingsProps> = ({ 
  metadata, 
  updateMetadata,
  onSave,
  content = '',
  title = ''
}) => {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  const handleInputChange = (field: string, value: any) => {
    updateMetadata(field, value);
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      const success = await onSave();
      if (success) {
        toast({
          title: "Settings Updated",
          description: "Your article settings have been saved.",
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="h-fit">
      <CardHeader>
        <CardTitle>Article Settings</CardTitle>
        <CardDescription>
          Configure metadata and publishing options
        </CardDescription>
      </CardHeader>

      <Tabs defaultValue="metadata">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="metadata">Metadata</TabsTrigger>
          <TabsTrigger value="publishing">Publishing</TabsTrigger>
          <TabsTrigger value="seo">SEO</TabsTrigger>
        </TabsList>
        
        <TabsContent value="metadata">
          <MetadataTab 
            metadata={metadata} 
            handleInputChange={handleInputChange} 
            content={content}
            title={title}
          />
        </TabsContent>
        
        <TabsContent value="publishing">
          <PublishingTab metadata={metadata} handleInputChange={handleInputChange} />
        </TabsContent>
        
        <TabsContent value="seo">
          <SeoTab metadata={metadata} handleInputChange={handleInputChange} />
        </TabsContent>
      </Tabs>

      <CardFooter className="flex justify-between">
        <Button variant="outline">Reset</Button>
        <Button 
          onClick={handleSaveSettings} 
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save Settings'}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default ArticleSettings;
